import { useNavigate, useParams } from 'react-router-dom';
import { UseCategoryRouteParamsReturn } from './interfaces';

/**
 * Reads category route parameters and builds category navigation destinations.
 *
 * ---
 * 🧠 **Context**
 *
 * - Used by both Header category variants through the existing Router; ids follow {@link UseCategoryRouteParamsReturn}.
 * - Subcategory destinations append the subcategorias query parameter.
 *
 * ---
 * @example
 * ```ts
 * const { goToCategory } = useCategoryRouteParams();
 * const openAnalgesics = () => goToCategory('medicamentos', 'analgesicos', 'adulto');
 * ```
 *
 * ---
 * @function useCategoryRouteParams
 * @returns {UseCategoryRouteParamsReturn} Current route ids and category navigation action.
 */
export function useCategoryRouteParams(): UseCategoryRouteParamsReturn {
	const { departmentId, categoryId, subcategoryId } = useParams<Record<string, string | undefined>>();

	const navigation = useNavigate();

	const goToCategory = (departmentId: string, categoryId: string, subcategoryId?: string) => {
		let path = `/departamentos/${departmentId}/categorias/${categoryId}`;

		if (subcategoryId) {
			path += `?subcategorias=${subcategoryId}`;
		}

		navigation(path);
	};

	return {
		departmentId,
		categoryId,
		subcategoryId,
		goToCategory,
	};
}
